import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import heroImage from '../../assets/images/rui13.jpg';
import logoTempoToOna from '../../assets/images/LOGO_TEMPO_TOO_ONA-01.png';

export function Hero() {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-sky-100 via-sky-50 to-white">
      {/* Soft Background Gradient - Blending with Sky Theme */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-sky-200/40 via-sky-100/30 to-cyan-50/20"
      />

      {/* Decorative Elements - Sky Theme */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Animated cloud-like decorative elements */}
        <motion.div 
          animate={{ x: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-20 left-10 w-32 h-16 bg-white/40 rounded-full blur-xl" 
        />
        <motion.div 
          animate={{ x: [0, -15, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-40 right-20 w-48 h-20 bg-white/30 rounded-full blur-xl" 
        />
        {/* Floating gradient orbs */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-sky-300/15 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            scale: [1.2, 1, 1.2],
            opacity: [0.15, 0.3, 0.15]
          }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-300/10 rounded-full blur-3xl"
        />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center min-h-[80vh]">
          {/* Left Side - Text Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center lg:text-left order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8"
            >
              {/* Logo Image */}
              <img
                src={logoTempoToOna}
                alt="Tempo To'o Ona"
                className="w-40 md:w-52 mx-auto lg:mx-0"
              />
            </motion.div>

            {/* Expandable Description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6"
            >
              {/* Short description preview */}
              <p className="text-sm md:text-base text-slate-600 leading-relaxed line-clamp-2 mb-3">
                <span dangerouslySetInnerHTML={{ __html: t('tempoToOna.description') }} />
              </p>

              {/* Expand/Collapse button */}
              <button
                onClick={() => setExpanded(!expanded)}
                className="inline-flex items-center gap-2 text-sky-600 font-semibold hover:text-sky-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 rounded"
              >
                {expanded ? (
                  <>
                    Show Less
                    <ChevronUp size={18} className="transition-transform duration-300" />
                  </>
                ) : (
                  <>
                    Show More
                    <ChevronDown size={18} className="transition-transform duration-300" />
                  </>
                )}
              </button>

              {/* Expanded content */}
              <motion.div
                initial={false}
                animate={{
                  height: expanded ? 'auto' : 0,
                  opacity: expanded ? 1 : 0,
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pt-3">
                  <p className="text-sm md:text-base text-slate-700 leading-relaxed">
                    <span dangerouslySetInnerHTML={{ __html: t('tempoToOna.description') }} />
                  </p>
                </div>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="font-heading font-black text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-tight mt-6 mb-6 text-slate-800"
            >
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-cyan-500">Dr. Rui Maria</span>              
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-cyan-500">de Araújo</span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-lg md:text-xl max-w-xl mx-auto lg:mx-0 mb-10 text-slate-700 leading-relaxed"
            >
              {t('hero.description')}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link
                to="/volunteer"
                className="btn-secondary text-center group"
              >
                <span className="flex items-center justify-center gap-2">
                  {t('hero.ctaPrimary')}
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    →
                  </motion.span>
                </span>
              </Link>
              {/* <Link
                to="/about"
                className="btn-secondary text-center"
              >
                {t('hero.ctaSecondary')}
              </Link> */}
            </motion.div>

            {/* Stats badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-wrap gap-4 justify-center lg:justify-start mt-8"
            >
              <div className="bg-white/60 backdrop-blur-sm px-5 py-3 rounded-xl border border-sky-200/50">
                <div className="text-2xl font-black text-sky-600">30+</div>
                <div className="text-xs uppercase tracking-wider text-slate-600">Years Service</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm px-5 py-3 rounded-xl border border-sky-200/50">
                <div className="text-2xl font-black text-sky-600">2002</div>
                <div className="text-xs uppercase tracking-wider text-slate-600">Independence</div>
              </div>
              <div className="bg-white/60 backdrop-blur-sm px-5 py-3 rounded-xl border border-sky-200/50">
                <div className="text-2xl font-black text-sky-600">2015</div>
                <div className="text-xs uppercase tracking-wider text-slate-600">Prime Minister</div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Hero Image */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="relative order-1 lg:order-2 flex justify-center lg:justify-end"
          >
            {/* Subtle animated background rings */}
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-[120%] h-[120%] border border-sky-300/10 rounded-full" />
            </motion.div>
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-[90%] h-[90%] border border-cyan-300/8 rounded-full" />
            </motion.div>
            
            {/* Soft glow effect behind image */}
            <motion.div
              animate={{ 
                scale: [1, 1.15, 1],
                opacity: [0.4, 0.6, 0.4]
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              className="absolute inset-0 bg-gradient-to-br from-sky-300/20 to-cyan-200/20 rounded-full blur-3xl"
            />
            
            {/* Floating particles */}
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ y: 0, opacity: 0 }}
                animate={{ 
                  y: [-20, 20, -20],
                  opacity: [0, 0.5, 0]
                }}
                transition={{ 
                  duration: 3 + i * 0.5, 
                  repeat: Infinity, 
                  ease: "easeInOut",
                  delay: i * 0.3
                }}
                className={`absolute w-2 h-2 bg-sky-400 rounded-full blur-sm`}
                style={{
                  left: `${20 + i * 15}%`,
                  top: `${30 + (i % 3) * 20}%`
                }}
              />
            ))}
            
            {/* Main Image Container - Clean and Clear */}
            <div className="relative z-10 w-full max-w-lg">
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="relative"
              >
                {/* Very subtle backdrop frame */}
                <div className="absolute -inset-3 bg-gradient-to-br from-sky-200/30 to-cyan-200/30 rounded-3xl blur-xl" />
                
                {/* Clean image with soft shadow */}
                <div className="relative rounded-3xl overflow-hidden">
                  <img
                    src={heroImage}
                    alt="Dr. Rui Maria de Araújo"
                    className="w-full h-auto object-cover drop-shadow-2xl"
                    style={{
                      filter: 'drop-shadow(0 25px 50px rgba(2, 132, 199, 0.15))'
                    }}
                  />
                </div>
                
                {/* Floating badge */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, delay: 1 }}
                  className="absolute -bottom-4 -right-4 bg-white/90 backdrop-blur-sm px-6 py-4 rounded-2xl shadow-xl border border-sky-200/50"
                >
                  <div className="text-center">
                    <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-cyan-500">2027</div>
                    <div className="text-xs uppercase tracking-wider text-slate-600 font-semibold">Vision</div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce"
      >
        <ChevronDown size={32} className="text-sky-600" />
      </motion.div>
    </section>
  );
}